import { forwardRef } from 'react';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { CustomContentProps, useSnackbar, VariantType } from 'notistack';
import i18n from '@/i18n';
import getColors from '@/theme/getColors';
import { withAlpha } from '@/theme/colorUtils';
import { ColorProps } from '@/theme/types/extended';

// Toast de EROARE interactiv (pipeline-ul global de erori, CTZ-2757): titlu pe
// categorie, contor „×N" la erorile repetate și click pe întregul toast → deschide
// dialogul de erori pe intrarea respectivă. Notistack pasează opțiunile custom din
// enqueueSnackbar direct componentei de conținut.
export interface IErrorToastMeta {
	title: string;
	count: number;
	onOpen: () => void;
	// override explicit de culoare (categorii fără variantă semantică în temă, ex.
	// galbenul pentru erorile FE runtime) — altfel culoarea vine din variantă
	colors?: { main: string; contrastText: string };
}

declare module 'notistack' {
	interface OptionsObject {
		errorToast?: IErrorToastMeta;
	}
}

const AUTO_HIDE_MS = 7000;

const getTitleForVariant = (variant: VariantType): string => {
	switch (variant) {
		case 'success':
			return 'Operațiune reușită!';
		case 'error':
			return 'Eroare!';
		case 'warning':
			return 'Atenție!';
		case 'info':
			return 'Informație';
		default:
			return 'Notificare';
	}
};

// Mapăm varianta notistack la culoarea din temă (getColors → lighter/light/main, ca la <Alert>).
const variantColor = (variant: VariantType): ColorProps => {
	switch (variant) {
		case 'success':
			return 'success';
		case 'error':
			return 'error';
		case 'warning':
			return 'warning';
		case 'info':
			return 'info';
		default:
			return 'primary';
	}
};

const getVariantIcon = (variant: VariantType) => {
	switch (variant) {
		case 'success':
			return <CheckCircleOutlineRounded fontSize="small" />;
		case 'error':
			return <ErrorOutlineRounded fontSize="small" />;
		case 'warning':
			return <WarningAmberRounded fontSize="small" />;
		default:
			return <InfoOutlined fontSize="small" />;
	}
};

export const NotificationSnackbar = forwardRef<
	HTMLDivElement,
	// eslint-disable-next-line react/require-default-props -- prop injectat de notistack; forwardRef nu expune defaultProps
	CustomContentProps & { errorToast?: IErrorToastMeta }
>((props, ref) => {
	const { id, message, variant = 'default', className, style, errorToast } = props;
	const theme = useTheme<Theme>();
	const { closeSnackbar } = useSnackbar();

	const palette = getColors(theme, variantColor(variant));
	const main = errorToast?.colors?.main ?? palette.main;
	const contrastText = errorToast?.colors?.contrastText ?? palette.contrastText;
	const title = errorToast?.title ?? getTitleForVariant(variant);

	// toast de eroare interactiv → tot cardul e clickabil și deschide dialogul
	const handleOpen = errorToast
		? () => {
				errorToast.onOpen();
				closeSnackbar(id);
			}
		: undefined;

	return (
		<Box
			ref={ref}
			className={className}
			style={style}
			role="alert"
			onClick={handleOpen}
			tabIndex={handleOpen ? 0 : undefined}
			aria-label={handleOpen ? `${title} — ${i18n.t('errors.toastOpenHint', { defaultValue: 'Apasă pentru detalii' })}` : undefined}
			onKeyDown={handleOpen ? (e) => e.key === 'Enter' && handleOpen() : undefined}
			sx={{
				width: 'min(420px, calc(100vw - 32px))',
				boxSizing: 'border-box',
				position: 'relative',
				display: 'flex',
				gap: 1.5,
				alignItems: 'flex-start',
				p: 2,
				pr: 5,
				borderRadius: 2,
				color: contrastText,
				backgroundColor: main,
				boxShadow: theme.customShadows.z1,
				overflow: 'hidden',
				cursor: handleOpen ? 'pointer' : 'default',
				'@keyframes gpSnackProgress': {
					from: { transform: 'scaleX(1)' },
					to: { transform: 'scaleX(0)' }
				}
			}}
		>
			<Box
				sx={{
					width: 34,
					height: 34,
					borderRadius: 1.5,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: contrastText,
					backgroundColor: withAlpha(contrastText, 0.2),
					flexShrink: 0
				}}
			>
				{getVariantIcon(variant)}
			</Box>

			<Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					<Typography variant="subtitle1" sx={{ color: contrastText, fontWeight: 600, lineHeight: 1.3 }}>
						{title}
					</Typography>
					{errorToast && errorToast.count > 1 && (
						<Chip
							size="small"
							label={`×${errorToast.count}`}
							sx={{
								height: 20,
								color: contrastText,
								backgroundColor: withAlpha(contrastText, 0.2),
								fontWeight: 600
							}}
						/>
					)}
				</Box>
				{typeof message === 'string' ? (
					<Typography variant="body2" sx={{ color: withAlpha(contrastText, 0.85), mt: 0.25 }}>
						{message}
					</Typography>
				) : (
					message
				)}
				{errorToast && (
					<Typography variant="caption" sx={{ color: withAlpha(contrastText, 0.7), mt: 0.5, display: 'block' }}>
						{i18n.t('errors.toastOpenHint', { defaultValue: 'Apasă pentru detalii' })}
					</Typography>
				)}
			</Box>

			<IconButton
				size="small"
				aria-label="Închide notificarea"
				onClick={(e) => {
					// pe toast-ul interactiv, închiderea NU trebuie să deschidă dialogul
					e.stopPropagation();
					closeSnackbar(id);
				}}
				sx={{
					position: 'absolute',
					top: 8,
					right: 8,
					color: withAlpha(contrastText, 0.85),
					'&:hover': { backgroundColor: withAlpha(contrastText, 0.16), color: contrastText }
				}}
			>
				<CloseRounded fontSize="small" />
			</IconButton>

			<Box
				sx={{
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 0,
					height: 3,
					backgroundColor: withAlpha(contrastText, 0.25)
				}}
			>
				<Box
					sx={{
						height: '100%',
						width: '100%',
						transformOrigin: 'left center',
						backgroundColor: contrastText,
						animation: `gpSnackProgress ${AUTO_HIDE_MS}ms linear forwards`
					}}
				/>
			</Box>
		</Box>
	);
});

NotificationSnackbar.displayName = 'NotificationSnackbar';
